import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Camera,
  ChevronRight,
  ClipboardList,
  Footprints,
  LayoutGrid,
  MapPin,
  Navigation,
  Phone,
  Plus,
} from 'lucide-react'
import { useState } from 'react'
import { NewQuoteDialog } from '@/components/quotes/new-quote-dialog'
import { PageHeader } from '@/components/shell/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  directionsUrl,
  siteLine,
  surveysQuery,
  telHref,
  type BookedSurvey,
  type Walkround,
} from '@/lib/enquiries'
import { ukDate } from '@/lib/quotes'
import { useTitle } from '@/lib/use-title'

export const Route = createFileRoute('/_app/surveys')({ component: SurveysPage })

const dayKey = (d: Date) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(d)
const time = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/London',
  }).format(new Date(iso))
const day = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Europe/London',
  }).format(new Date(iso))

/** Group bookings as Earlier (not started yet), Today, This week and Later. */
function group(items: BookedSurvey[], now = new Date()) {
  const today = dayKey(now)
  const weekEnd = dayKey(new Date(now.getTime() + 7 * 86_400_000))
  const out = {
    earlier: [] as BookedSurvey[],
    today: [] as BookedSurvey[],
    week: [] as BookedSurvey[],
    later: [] as BookedSurvey[],
  }
  for (const s of items) {
    const k = dayKey(new Date(s.surveyAt))
    if (k < today) out.earlier.push(s)
    else if (k === today) out.today.push(s)
    else if (k <= weekEnd) out.week.push(s)
    else out.later.push(s)
  }
  return out
}

function SurveysPage() {
  useTitle('Site surveys')
  const { data, isPending, isError, error } = useQuery(surveysQuery())
  const [creating, setCreating] = useState(false)
  const g = group(data?.booked ?? [])

  return (
    <>
      <PageHeader
        title="Site surveys"
        description="Booked visits and walk-rounds in progress."
        actions={
          <Button className="h-11 md:h-10" onClick={() => setCreating(true)}>
            <Plus />
            Start a walk-round
          </Button>
        }
      />
      {isError ? (
        <p role="alert" className="text-destructive">
          {error.message}
        </p>
      ) : isPending ? (
        <div className="grid gap-3">
          <Skeleton className="h-28 rounded-xl" />
          <Skeleton className="h-28 rounded-xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <section
            aria-labelledby="booked-h"
            className="grid min-w-0 grid-cols-1 content-start gap-4"
          >
            <h2 id="booked-h" className="flex items-center gap-2 text-lg font-semibold">
              <ClipboardList className="size-5 text-primary" aria-hidden />
              Booked surveys
            </h2>
            {data.booked.length === 0 ? (
              <p className="rounded-xl border bg-card p-5 text-[15px] text-muted-foreground">
                No surveys booked. Book one from an{' '}
                <Link
                  to="/enquiries"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  enquiry
                </Link>
                .
              </p>
            ) : (
              <>
                <Group title="Earlier, not started yet" items={g.earlier} tone="warn" />
                <Group title="Today" items={g.today} />
                <Group title="Next 7 days" items={g.week} />
                <Group title="Later" items={g.later} />
              </>
            )}
          </section>

          <section
            aria-labelledby="walk-h"
            className="grid min-w-0 grid-cols-1 content-start gap-4"
          >
            <h2 id="walk-h" className="flex items-center gap-2 text-lg font-semibold">
              <Footprints className="size-5 text-primary" aria-hidden />
              Walk-rounds in progress
            </h2>
            {data.walkrounds.length === 0 ? (
              <p className="rounded-xl border bg-card p-5 text-[15px] text-muted-foreground">
                No on-site quotes in progress.
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-3">
                {data.walkrounds.map((w) => (
                  <WalkroundCard key={w.quoteId} w={w} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
      <NewQuoteDialog open={creating} onOpenChange={setCreating} />
    </>
  )
}

function Group({ title, items, tone }: { title: string; items: BookedSurvey[]; tone?: 'warn' }) {
  if (items.length === 0) return null
  return (
    <div className="grid grid-cols-1 gap-2">
      <h3
        className={`text-sm font-semibold tracking-wide uppercase ${tone === 'warn' ? 'text-[#7a4d0e] dark:text-warning' : 'text-muted-foreground'}`}
      >
        {title}
      </h3>
      <ul className="grid grid-cols-1 gap-3">
        {items.map((s) => (
          <li
            key={s.enquiryId}
            className="rounded-xl border bg-card p-4"
            data-testid="booked-survey"
          >
            <div className="flex items-start gap-4">
              <div className="w-16 shrink-0 text-center">
                <p className="tabular text-lg leading-tight font-semibold">{time(s.surveyAt)}</p>
                <p className="text-[13px] text-muted-foreground">{day(s.surveyAt)}</p>
              </div>
              <div className="min-w-0 flex-1">
                <Link
                  to="/enquiries/$id"
                  params={{ id: s.enquiryId }}
                  className="block truncate font-semibold hover:underline"
                >
                  {s.companyName || s.contactName}
                </Link>
                <p className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                  <span className="break-words">{siteLine(s) || 'No address yet'}</span>
                </p>
                {s.services && <p className="mt-1 line-clamp-2 text-sm">{s.services}</p>}
                <p className="mt-1 text-[13px] text-muted-foreground">
                  {s.reference}
                  {s.contactName && s.companyName ? ` · ${s.contactName}` : ''}
                  {s.assignedName ? ` · ${s.assignedName}` : ''}
                </p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {s.phone ? (
                <Button
                  variant="outline"
                  className="h-11"
                  render={<a href={telHref(s.phone)} />}
                  aria-label={`Call ${s.contactName || s.companyName}`}
                >
                  <Phone />
                  Call
                </Button>
              ) : (
                <span />
              )}
              {s.postcode || s.address ? (
                <Button
                  variant="outline"
                  className="h-11"
                  render={<a href={directionsUrl(s)} target="_blank" rel="noopener noreferrer" />}
                >
                  <Navigation />
                  Directions
                </Button>
              ) : (
                <span />
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}

function WalkroundCard({ w }: { w: Walkround }) {
  return (
    <li>
      <Link
        to="/quotes/$id/survey"
        params={{ id: w.quoteId }}
        className="flex items-center gap-3 rounded-xl border bg-card p-4 hover:border-primary/40"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-semibold">{w.customerName || w.title}</span>
          {w.siteAddress && (
            <span className="block truncate text-sm text-muted-foreground">{w.siteAddress}</span>
          )}
          <span className="mt-1 flex flex-wrap gap-x-3 text-[13px] text-muted-foreground">
            <span className="tabular">{w.reference}</span>
            <span className="inline-flex items-center gap-1">
              <LayoutGrid className="size-3.5" aria-hidden />
              {w.areaCount} {w.areaCount === 1 ? 'area' : 'areas'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Camera className="size-3.5" aria-hidden />
              {w.photoCount} {w.photoCount === 1 ? 'photo' : 'photos'}
            </span>
            <span>Updated {ukDate(w.updatedAt)}</span>
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-primary">
          <span className="max-sm:sr-only">Continue walk-round</span>
          <ChevronRight className="size-4" aria-hidden />
        </span>
      </Link>
    </li>
  )
}
